import type {
    BalancePicture,
    BreakEvenMetric,
    CashCycleMetric,
    CashCoverageMetric,
    CashRunwayMetric,
    FinancialPositionMissing,
    FinancialPositionReadiness,
    FinancialPositionState,
    FinancialPositionTone,
    FinancialPositionZone,
    GetFinancialPositionResponse,
    GmroiMetric,
    NetWorkingCapitalMetric,
} from '@/api/models';

/**
 * The boundary between the wire and the domain shapes.
 *
 * bos-api serialises this payload in the names the client already uses, so this
 * is not a rename layer — it is the **guard**. A metric arriving without its
 * `state`, or claiming `COMPUTED` while its figures are null, is contract drift,
 * and the one thing this screen must never do is render an invented figure as
 * though it were the position. Anything unreadable is refused whole, and the
 * card's boundary shows its retry.
 */

type Wire = Record<string, unknown>;

const isObject = (value: unknown): value is Wire =>
    typeof value === 'object' && value !== null;

const STATES: FinancialPositionState[] = ['COMPUTED', 'PENDING'];
const TONES: FinancialPositionTone[] = ['STRAINED', 'STABLE', 'STRONG'];
const MISSING: FinancialPositionMissing[] = [
    'NO_LEDGER_BALANCES',
    'NO_CASH_ACCOUNT',
    'NO_DUE_DATES',
    'NO_FIXED_COST',
    'NO_CASH_REVENUE',
    'NO_TRADING_HISTORY',
    'NO_INVENTORY',
];

const asState = (value: unknown): FinancialPositionState | null =>
    STATES.find((state) => state === value) ?? null;

const asTone = (value: unknown): FinancialPositionTone | null =>
    TONES.find((tone) => tone === value) ?? null;

/**
 * Unknown reason codes are **dropped, not rejected**. A server that grows a
 * fifth reason should not blank the screen for a client that has not shipped
 * its wording yet — the metric is still pending, and the card falls back to its
 * generic sentence.
 */
const asMissing = (value: unknown): FinancialPositionMissing[] =>
    Array.isArray(value)
        ? value.flatMap((entry) => MISSING.filter((code) => code === entry))
        : [];

/** A nullable integer. Anything non-numeric reads as absent, never as zero. */
const asNumber = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const asBoolean = (value: unknown): boolean => value === true;

type MetricBase = {
    state: FinancialPositionState;
    tone: FinancialPositionTone | null;
    missing: FinancialPositionMissing[];
};

const mapMetricBase = (wire: unknown): MetricBase | null => {
    if (!isObject(wire)) {
        return null;
    }
    const state = asState(wire.state);
    if (!state) {
        return null;
    }
    return { state, tone: asTone(wire.tone), missing: asMissing(wire.missing) };
};

const mapNetWorkingCapital = (
    wire: unknown,
): NetWorkingCapitalMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    return {
        ...base,
        assetsTetri: asNumber(wire.assetsTetri),
        liabilitiesTetri: asNumber(wire.liabilitiesTetri),
        differenceTetri: asNumber(wire.differenceTetri),
        deltaTetri: asNumber(wire.deltaTetri),
    };
};

const mapCashCoverage = (wire: unknown): CashCoverageMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    return {
        ...base,
        cashTetri: asNumber(wire.cashTetri),
        obligationsTetri: asNumber(wire.obligationsTetri),
        ratio: asNumber(wire.ratio),
        deltaRatio: asNumber(wire.deltaRatio),
    };
};

const mapBreakEven = (wire: unknown): BreakEvenMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    const daysInMonth = asNumber(wire.daysInMonth);
    if (daysInMonth === null) {
        // The bar is drawn against the month's length whether or not the
        // crossing day is known, so this one is not optional.
        return null;
    }
    return {
        ...base,
        revenueTetri: asNumber(wire.revenueTetri),
        resultTetri: asNumber(wire.resultTetri),
        breakEvenDay: asNumber(wire.breakEvenDay),
        daysInMonth,
        deltaDays: asNumber(wire.deltaDays),
    };
};

const mapCashRunway = (wire: unknown): CashRunwayMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    return {
        ...base,
        days: asNumber(wire.days),
        cashTetri: asNumber(wire.cashTetri),
        dailyFixedCostTetri: asNumber(wire.dailyFixedCostTetri),
        deltaDays: asNumber(wire.deltaDays),
    };
};

const mapCashCycle = (wire: unknown): CashCycleMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    return {
        ...base,
        days: asNumber(wire.days),
        dioDays: asNumber(wire.dioDays),
        dsoDays: asNumber(wire.dsoDays),
        dpoDays: asNumber(wire.dpoDays),
    };
};

const mapGmroi = (wire: unknown): GmroiMetric | null => {
    const base = mapMetricBase(wire);
    if (!base || !isObject(wire)) {
        return null;
    }
    return { ...base, value: asNumber(wire.value) };
};

const mapBalance = (wire: unknown): BalancePicture | null => {
    if (!isObject(wire)) {
        return null;
    }
    const state = asState(wire.state);
    const cashCycle = mapCashCycle(wire.cashCycle);
    const gmroi = mapGmroi(wire.gmroi);
    if (!state || !cashCycle || !gmroi) {
        return null;
    }

    const window = isObject(wire.window) ? wire.window : null;

    return {
        state,
        missing: asMissing(wire.missing),
        window:
            window &&
            typeof window.from === 'string' &&
            typeof window.to === 'string'
                ? { from: window.from, to: window.to }
                : null,
        currentAssetsTetri: asNumber(wire.currentAssetsTetri),
        currentLiabilitiesTetri: asNumber(wire.currentLiabilitiesTetri),
        workingCapitalTetri: asNumber(wire.workingCapitalTetri),
        workingCapitalTone: asTone(wire.workingCapitalTone),
        cashCycle,
        gmroi,
    };
};

const mapZone = (wire: unknown): FinancialPositionZone | null => {
    if (!isObject(wire)) {
        return null;
    }
    const state = asState(wire.state);
    if (!state) {
        return null;
    }
    const history = Array.isArray(wire.history) ? wire.history : [];
    return {
        state,
        tone: asTone(wire.tone),
        summary: typeof wire.summary === 'string' ? wire.summary : null,
        history: history.flatMap((entry) => {
            if (!isObject(entry) || typeof entry.month !== 'string') {
                return [];
            }
            const tone = asTone(entry.tone);
            return tone ? [{ month: entry.month, tone }] : [];
        }),
    };
};

const mapReadiness = (wire: unknown): FinancialPositionReadiness => {
    const source = isObject(wire) ? wire : {};
    const expenses = isObject(source.expenses) ? source.expenses : {};
    return {
        sales: {
            met: asBoolean(isObject(source.sales) ? source.sales.met : false),
        },
        expenses: {
            met: asBoolean(expenses.met),
            configured: asNumber(expenses.configured) ?? 0,
            required: asNumber(expenses.required) ?? 0,
        },
        bank: {
            met: asBoolean(isObject(source.bank) ? source.bank.met : false),
        },
    };
};

/** Null when the payload is not a financial position this client can read. */
export const mapFinancialPosition = (
    wire: unknown,
): GetFinancialPositionResponse | null => {
    if (!isObject(wire) || typeof wire.asOf !== 'string') {
        return null;
    }

    const zone = mapZone(wire.zone);
    const metrics = isObject(wire.metrics) ? wire.metrics : null;
    if (!zone || !metrics) {
        return null;
    }

    const netWorkingCapital = mapNetWorkingCapital(metrics.netWorkingCapital);
    const cashCoverage = mapCashCoverage(metrics.cashCoverage);
    const breakEven = mapBreakEven(metrics.breakEven);
    const cashRunway = mapCashRunway(metrics.cashRunway);

    const balance = mapBalance(wire.balance);

    if (
        !netWorkingCapital ||
        !cashCoverage ||
        !breakEven ||
        !cashRunway ||
        !balance
    ) {
        return null;
    }

    return {
        asOf: wire.asOf,
        zone,
        readiness: mapReadiness(wire.readiness),
        metrics: { netWorkingCapital, cashCoverage, breakEven, cashRunway },
        balance,
    };
};
